import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ShoppingCart, Menu, X, User, LogOut, Phone, Mail, ChevronDown, Zap, Truck } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { CONTACT_INFO, categories } from '../../data/mock';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const { cartCount, openCart } = useCart();
  const { user, isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setShowCategories(false);
  }, [location]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      {/* Announcement Bar - Cyan Accent */}
      <div className="bg-gradient-to-r from-neutral-950 via-axon-950 to-neutral-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center py-2 gap-4 md:gap-8 text-xs md:text-sm">
            <span className="hidden sm:flex items-center gap-2 text-axon-400 font-medium">
              <Truck className="w-4 h-4" />
              FREE SHIPPING $99+
            </span>
            <span className="hidden md:block text-neutral-700">|</span>
            <a 
              href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`}
              className="flex items-center gap-1.5 text-neutral-300 hover:text-axon-400 transition-colors"
            >
              <Phone className="w-3.5 h-3.5" />
              <span className="font-medium">{CONTACT_INFO.phone}</span>
            </a>
            <span className="hidden sm:block text-neutral-700">|</span>
            <a 
              href={`mailto:${CONTACT_INFO.email}`}
              className="hidden sm:flex items-center gap-1.5 text-neutral-300 hover:text-axon-400 transition-colors"
            >
              <Mail className="w-3.5 h-3.5" />
              <span className="font-medium">{CONTACT_INFO.email}</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation - Dark Theme */}
      <nav className={`transition-all duration-300 ${isScrolled 
        ? 'bg-neutral-950/98 backdrop-blur-xl shadow-2xl border-b border-neutral-800' 
        : 'bg-neutral-950/95 backdrop-blur-md'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo - Axon Muscle Branding */}
            <Link to="/" className="flex items-center gap-3 group">
              <div className="relative">
                <div className="w-12 h-12 bg-gradient-to-br from-axon-600 to-axon-500 rounded-xl flex items-center justify-center group-hover:shadow-glow-cyan transition-all duration-300 transform group-hover:scale-105">
                  <Zap className="w-6 h-6 text-white" />
                </div>
              </div>
              <div className="flex flex-col">
                <span className="font-display text-xl font-bold text-white tracking-wide uppercase leading-none">
                  Axon
                </span>
                <span className="text-[10px] font-semibold text-axon-500 tracking-[0.2em] uppercase">
                  Muscle
                </span>
              </div>
            </Link>

            {/* Desktop Navigation - Clean Horizontal */}
            <div className="hidden lg:flex items-center gap-1">
              <Link
                to="/"
                className={`px-5 py-2.5 text-sm font-semibold uppercase tracking-wider transition-all ${
                  location.pathname === '/'
                    ? 'text-axon-500'
                    : 'text-neutral-300 hover:text-white'
                }`}
              >
                Home
              </Link>
              
              {/* Shop Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowCategories(!showCategories)}
                  className={`flex items-center gap-1 px-5 py-2.5 text-sm font-semibold uppercase tracking-wider transition-all ${
                    location.pathname.includes('/shop')
                      ? 'text-axon-500'
                      : 'text-neutral-300 hover:text-white'
                  }`}
                >
                  Shop
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${showCategories ? 'rotate-180' : ''}`} />
                </button>
                
                {showCategories && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowCategories(false)} />
                    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-56 bg-neutral-900 rounded-xl shadow-2xl border border-neutral-800 py-2 z-20 animate-fade-up">
                      <Link
                        to="/shop"
                        className="block px-5 py-3 text-white hover:bg-axon-600 font-semibold text-sm border-b border-neutral-800 mx-2 rounded-lg transition-colors"
                      >
                        All Products
                      </Link>
                      <div className="py-2">
                        {categories.map((cat) => (
                          <Link
                            key={cat.id}
                            to={`/shop?category=${cat.slug}`}
                            className="block px-5 py-2.5 text-neutral-400 hover:bg-neutral-800 hover:text-white transition-colors text-sm mx-2 rounded-lg"
                          >
                            {cat.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
              
              <Link
                to="/about"
                className={`px-5 py-2.5 text-sm font-semibold uppercase tracking-wider transition-all ${
                  location.pathname === '/about'
                    ? 'text-axon-500'
                    : 'text-neutral-300 hover:text-white'
                }`}
              >
                About
              </Link>
              <Link
                to="/contact"
                className={`px-5 py-2.5 text-sm font-semibold uppercase tracking-wider transition-all ${
                  location.pathname === '/contact'
                    ? 'text-axon-500'
                    : 'text-neutral-300 hover:text-white'
                }`}
              >
                Contact
              </Link>
              <a
                href="https://affiliate.axonmuscle.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="px-5 py-2.5 text-sm font-semibold uppercase tracking-wider transition-all text-neutral-300 hover:text-white"
              >
                Become an Affiliate
              </a>
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-4">
              {/* Auth */}
              <div className="hidden lg:flex items-center">
                {isAuthenticated ? (
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-neutral-400 font-medium">{user?.firstName}</span>
                    <button
                      onClick={handleLogout}
                      className="p-2 text-neutral-500 hover:text-axon-500 transition-colors"
                      title="Sign Out"
                    >
                      <LogOut className="w-5 h-5" />
                    </button>
                  </div>
                ) : (
                  <Link
                    to="/login"
                    className="flex items-center gap-2 px-4 py-2 text-sm text-neutral-400 hover:text-white transition-colors"
                  >
                    <User className="w-4 h-4" />
                    <span className="font-medium">Sign In</span>
                  </Link>
                )}
              </div>

              {/* Cart - Cyan Button */}
              <button
                onClick={openCart}
                className="relative p-3 bg-gradient-to-r from-axon-600 to-axon-500 hover:from-axon-500 hover:to-axon-400 rounded-xl text-white transition-all shadow-button hover:shadow-glow-cyan group"
                data-testid="cart-button"
              >
                <ShoppingCart className="w-5 h-5 group-hover:scale-110 transition-transform" />
                {cartCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-electric-500 text-white text-xs font-bold flex items-center justify-center rounded-full shadow-lg animate-scale-in">
                    {cartCount}
                  </span>
                )}
              </button>

              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2 text-neutral-400 hover:text-white transition-colors"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu - Dark Theme */}
        <div className={`lg:hidden transition-all duration-300 overflow-hidden ${
          isMobileMenuOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
        }`}>
          <div className="bg-neutral-900 border-t border-neutral-800 px-4 py-6 space-y-1">
            <Link
              to="/"
              className={`block px-4 py-3 rounded-xl font-semibold uppercase tracking-wider text-sm transition-colors ${
                location.pathname === '/' ? 'bg-axon-600 text-white' : 'text-neutral-300 hover:bg-neutral-800'
              }`}
            >
              Home
            </Link>
            <Link
              to="/shop"
              className={`block px-4 py-3 rounded-xl font-semibold uppercase tracking-wider text-sm transition-colors ${
                location.pathname === '/shop' ? 'bg-axon-600 text-white' : 'text-neutral-300 hover:bg-neutral-800'
              }`}
            >
              All Products
            </Link>
            
            {/* Mobile Categories */}
            <div className="pl-4 space-y-0.5 py-2">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/shop?category=${cat.slug}`}
                  className="block px-4 py-2 text-sm text-neutral-500 hover:text-axon-400 transition-colors"
                >
                  {cat.name}
                </Link>
              ))}
            </div>
            
            <Link
              to="/about"
              className={`block px-4 py-3 rounded-xl font-semibold uppercase tracking-wider text-sm transition-colors ${
                location.pathname === '/about' ? 'bg-axon-600 text-white' : 'text-neutral-300 hover:bg-neutral-800'
              }`}
            >
              About
            </Link>
            <Link
              to="/contact"
              className={`block px-4 py-3 rounded-xl font-semibold uppercase tracking-wider text-sm transition-colors ${
                location.pathname === '/contact' ? 'bg-axon-600 text-white' : 'text-neutral-300 hover:bg-neutral-800'
              }`}
            >
              Contact
            </Link>
            <a
              href="https://affiliate.axonmuscle.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="block px-4 py-3 rounded-xl font-semibold uppercase tracking-wider text-sm transition-colors text-neutral-300 hover:bg-neutral-800"
            >
              Become an Affiliate
            </a>
            
            <div className="pt-4 border-t border-neutral-800 mt-4">
              {isAuthenticated ? (
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 w-full px-4 py-3 text-neutral-300 hover:bg-neutral-800 rounded-xl transition-colors"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="font-medium">Sign Out</span>
                </button>
              ) : (
                <Link
                  to="/login"
                  className="flex items-center gap-3 px-4 py-3 text-neutral-300 hover:bg-neutral-800 rounded-xl transition-colors"
                >
                  <User className="w-5 h-5" />
                  <span className="font-medium">Sign In</span>
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
